/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-FormattedMessage */
/** @jsx jsx */
import { styled, css, jsx } from '@compiled/react';
import React, { type FC, useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import LinkBrokenIconMigration from '@atlaskit/icon/core/link-broken';
import UFOInteractionIgnore from '@atlaskit/react-ufo/interaction-ignore/ufo-interaction-ignore';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import SectionMessage from '@atlaskit/section-message/message';
import Link from '@atlaskit/link/link';

import { fg } from '@confluence/feature-gating';

import type { ExtensionNodeType } from '../../extensions-common/extensionNodeTypes.ts';

import { MacroIcon } from './MacroIcon.tsx';
// eslint-disable-next-line import/no-cycle
import { getExtensionBodyType } from './manifest-helpers.tsx';
import type { LegacyMacroManifest } from './extensionTypes.ts';

const i18n = defineMessages({
	invalidAnchorMacroLabel: {
		id: 'fabric-extension-lib.anchor.placeholder.icon-label',
		defaultMessage: 'Invalid anchor link',
		description: `Label text for the icon shown in an invalid anchor macro's body`,
	},
	deprecatedExtensionPlaceholderTitle: {
		id: 'fabric-extension-lib.deprecated-extension-placeholder.title',
		defaultMessage: 'Deprecated macro',
		description:
			'Title within the deprecated extension placeholder, which will replace instances of deprecated macros on a page',
	},
	deprecatedExtensionPlaceholderDescription: {
		id: 'fabric-extension-lib.deprecated-extension-placeholder.description',
		defaultMessage: 'The {macroName} macro is no longer available in Confluence Cloud.',
		description:
			'Description within the deprecated extension placeholder, where {macroName} is the name of the deprecated macro that is being replaced by the placeholder',
	},
	deprecatedExtensionPlaceholderLink: {
		id: 'fabric-extension-lib.deprecated-extension-placeholder.link',
		defaultMessage: 'Learn more.',
		description:
			'Link text in the deprecated extension placeholder description that will take users to the community announcement post about the deprecation',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemBody = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

const descriptionStyledSelector = 'description-styled-selector';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span<{ 'data-styled-selector'?: string }>({
	font: token('font.body.small'),
	color: token('color.text.subtle'),
	marginTop: token('space.050'),
});
Description.defaultProps = {
	'data-styled-selector': descriptionStyledSelector,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Item = styled.div<{ isEditor: boolean }>({
	background: token('elevation.surface'),

	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: (props) => (props.isEditor ? token('space.300') : token('space.0')),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineItem = styled.div({
	background: token('color.background.accent.gray.subtlest'),
	display: 'inline-flex',
	alignItems: 'center',
	padding: `1px 0.24em ${token('space.025')} 0.24em`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	font: token('font.body.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	p: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		[`[data-styled-selector="${descriptionStyledSelector}"]`]: {
			marginLeft: token('space.100'),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemIcon = styled.div<{ width: number; height: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${(props) => props.width}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${(props) => props.height}px`,
	overflow: 'hidden',
	border: token('color.border'),

	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	boxSizing: 'border-box',
	background: token('elevation.surface'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: (props) => (props.width > 32 ? token('space.200') : token('space.050')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: `${(props) => props.width}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: `${(props) => props.height}px`,
	},
});

const anchorMacroPlaceholderStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: token('color.background.neutral'),
	width: '24px',
	height: '20px',
	overflow: 'hidden',
	borderRadius: token('radius.small'),
	boxSizing: 'border-box',
});

export const getIconComponent = (macro: LegacyMacroManifest, testId?: string): JSX.Element => {
	return <MacroIcon macro={macro} testId={testId} />;
};

export const AnchorMacroPlaceholder = (): JSX.Element => {
	const intl = useIntl();
	return (
		<div css={anchorMacroPlaceholderStyles}>
			<LinkBrokenIconMigration
				label={intl.formatMessage(i18n.invalidAnchorMacroLabel)}
				color={token('color.icon.danger')}
			/>
		</div>
	);
};

const InlineExtensionPlaceholder = ({
	macro,
	description,
}: {
	macro: LegacyMacroManifest;
	description?: React.ReactNode;
}) => {
	return (
		<UFOLoadHold name="inline-extension-placeholder">
			<InlineItem data-testid="inline-extension-placeholder">
				<ItemIcon width={16} height={16}>
					<MacroIcon macro={macro} size="small" />
				</ItemIcon>

				<ItemBody>
					<Text as="p">
						{macro.title}
						{description && <Description>{description}</Description>}
					</Text>
				</ItemBody>
			</InlineItem>
		</UFOLoadHold>
	);
};

type macroPlaceholder = {
	title: string;
	macroName: string;
	icon: {
		width: number;
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		location: string;
		height: number;
		relative: boolean;
	};
};

export const ExtensionPlaceholder = ({
	macro,
	description,
	isEditor,
}: {
	macro: LegacyMacroManifest | macroPlaceholder;
	description?: React.ReactNode;
	isEditor: boolean;
}): JSX.Element => {
	return (
		<UFOLoadHold name="extension-placeholder">
			<Item isEditor={isEditor} data-testid="extension-placeholder">
				<ItemIcon width={40} height={40}>
					<MacroIcon macro={macro} size="medium" />
				</ItemIcon>
				<ItemBody>
					<Heading size="small">{macro.title}</Heading>
					{description && <Description>{description}</Description>}
				</ItemBody>
			</Item>
		</UFOLoadHold>
	);
};

export const editToSetupMessage: JSX.Element = (
	<FormattedMessage
		id="confluence-fabric-editor.extension.placeholder.missing-config"
		description="Message shown when a macro is inserted but can't be rendered due to missing configuration"
		defaultMessage="Edit to setup"
	/>
);

interface PlaceholderWithAnalyticsProps {
	macro: LegacyMacroManifest;
	extension: ExtensionParams<ExtensionMacroParams>;
	isEditor: boolean;
}
const PlaceholderWithAnalytics = ({
	macro,
	extension,
	isEditor,
}: PlaceholderWithAnalyticsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useMemo(() => {
		if (createAnalyticsEvent) {
			const { extensionKey, extensionType, parameters } = extension;
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'macro',
					actionSubjectId: 'placeholder',
					source: isEditor ? 'editor' : 'renderer',
					attributes: {
						extensionKey,
						extensionType,
						isMissingRequiredParameters: !!parameters?.macroParams?.isMissingRequiredParameters,
					},
				},
			}).fire();
		}
		// NOTE: `extension` node changes on resize so can't use it as dependency
		// A deep check can be done using `extension.macroMetadata.macroId` though not needed
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createAnalyticsEvent, macro, isEditor]);

	if (macro.macroName === 'anchor') {
		return <AnchorMacroPlaceholder />;
	}

	if (getExtensionBodyType(macro) === 'inlineExtension') {
		return (
			<InlineExtensionPlaceholder macro={macro} description={<span>({editToSetupMessage})</span>} />
		);
	}

	return (
		<ExtensionPlaceholder isEditor={isEditor} macro={macro} description={editToSetupMessage} />
	);
};

export type ExtensionMacroParams = Parameters & {
	macroParams?: { isMissingRequiredParameters?: boolean };
};

export const getMissingConfigPlaceholderComponent = (
	macro: LegacyMacroManifest,
	extension: ExtensionParams<ExtensionMacroParams>,
	isEditor: boolean,
): JSX.Element => (
	<UFOInteractionIgnore>
		<PlaceholderWithAnalytics macro={macro} extension={extension} isEditor={isEditor} />
	</UFOInteractionIgnore>
);

export const getNonRenderablePlaceholderComponent = (
	macro: LegacyMacroManifest,
	type: ExtensionNodeType,
	isLivePage?: boolean,
): JSX.Element => {
	if (type === 'inlineExtension') {
		return (
			<UFOInteractionIgnore>
				<InlineExtensionPlaceholder macro={macro} />
			</UFOInteractionIgnore>
		);
	}

	return (
		<UFOInteractionIgnore>
			<ExtensionPlaceholder
				isEditor
				macro={macro}
				description={
					isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in') ? (
						<FormattedMessage
							id="confluence-fabric-editor.extension.placeholder.can-not-render-in-live-page-editor"
							description="Message shown when a macro is inserted but can't be rendered because it doesn't work in a live page"
							defaultMessage="The results of this element aren’t visible when editing a live doc. Convert this doc to a page and publish it via the <menuIcon></menuIcon> menu to display it fully."
							values={{
								menuIcon: () => <MoreIcon label="Icon for publish menu" />,
							}}
						/>
					) : (
						<FormattedMessage
							id="confluence-fabric-editor.extension.placeholder.can-not-render-in-editor"
							description="Message shown when a macro is inserted but can't be rendered because it doesn't work in edit mode"
							defaultMessage="The results of this element aren’t visible when editing. Preview the page or publish it to see how it will look."
						/>
					)
				}
			/>
		</UFOInteractionIgnore>
	);
};

type DeprecatedExtensionPlaceholderProps = {
	macroName: string;
};
const deprecatedExtensionPlaceholderStyles = css({
	font: token('font.heading.xxsmall'),
});

export const DeprecatedExtensionPlaceholder: FC<DeprecatedExtensionPlaceholderProps> = ({
	macroName,
}) => {
	const { formatMessage } = useIntl();

	return (
		<UFOInteractionIgnore>
			<div css={deprecatedExtensionPlaceholderStyles}>
				<SectionMessage
					appearance="warning"
					title={formatMessage(i18n.deprecatedExtensionPlaceholderTitle)}
					testId="deprecated-extension-placeholder"
				>
					{formatMessage(i18n.deprecatedExtensionPlaceholderDescription, {
						macroName,
					})}
					&nbsp;
					<Link
						target="_blank"
						href="https://community.atlassian.com/forums/Confluence-articles/Deprecating-infrequently-used-macros-in-Confluence-Cloud/ba-p/2981360"
					>
						{formatMessage(i18n.deprecatedExtensionPlaceholderLink)}
					</Link>
				</SectionMessage>
			</div>
		</UFOInteractionIgnore>
	);
};
